<template>
  <div class="creation-tools-page">
    <a-tabs v-model:activeKey="activeTab">
      <a-tab-pane key="video" tab="视频工具">
        <div class="tool-wrap">
          <a-card title="视频转 WebP 工具" :bordered="false" class="tool-card">
            <p class="page-desc">上传 MP4 视频，自动转换为 WebP 格式图片。</p>

            <ConvertVideo
              v-model:videoURL="videoURL"
              v-model:webpURL="webpURL"
            />

            <div v-if="videoURL || webpURL" class="result-section">
              <a-divider>转换结果</a-divider>
              <div class="media-row">
                <div v-if="videoURL" class="media-card">
                  <div class="media-header">
                    <span class="media-title">原视频</span>
                  </div>
                  <div class="media-body">
                    <video :src="videoURL" controls class="media-content" />
                  </div>
                  <div class="media-footer">{{ videoURL }}</div>
                </div>

                <div v-if="webpURL" class="media-card">
                  <div class="media-header">
                    <span class="media-title">转换后 (WebP)</span>
                    <span class="media-actions">
                      <a-tooltip title="最大化查看">
                        <a-button type="text" size="small" @click="previewVisible = true">
                          <template #icon><FullscreenOutlined /></template>
                        </a-button>
                      </a-tooltip>
                      <a-tooltip title="下载">
                        <a-button type="text" size="small" @click="downloadWebp">
                          <template #icon><DownloadOutlined /></template>
                        </a-button>
                      </a-tooltip>
                    </span>
                  </div>
                  <div class="media-body" @click="previewVisible = true">
                    <img :src="webpURL" alt="webp" class="media-content media-img" />
                  </div>
                  <div class="media-footer">{{ webpURL }}</div>
                </div>
              </div>
            </div>
          </a-card>
        </div>
      </a-tab-pane>

      <a-tab-pane key="image" tab="图片工具">
        <a-empty description="暂无图片工具" style="margin-top: 60px;" />
      </a-tab-pane>

      <a-tab-pane key="pixverse" tab="Pixverse 工具">
        <div class="tool-wrap">
          <a-card title="Pixverse提交任务" :bordered="false" class="tool-card">
            <p class="page-desc">使用 Pixverse 工具处理视频。</p>

            <a-form :model="pixverseForm" layout="vertical">
              <a-form-item label="原始视频路径" name="oriVideoPath">
                <UploadVideo v-model:videoURL="pixverseForm.oriVideoPath" />
                <a-input v-model:value="pixverseForm.oriVideoPath" placeholder="原始视频路径" />
              </a-form-item>
              <a-form-item label="参考图片路径" name="referImgPath">
                <Upload v-model:imageUrl="pixverseForm.referImgPath" />
                <a-input v-model:value="pixverseForm.referImgPath" placeholder="参考图片路径" />
              </a-form-item>
              <a-form-item label="提示词" name="prompt" required>
                <a-input v-model:value="pixverseForm.prompt" placeholder="提示词" />
              </a-form-item>
              <a-form-item label="自定义Mask" name="maskURL">
                <a-input v-model:value="pixverseForm.maskURL" placeholder="自定义Mask" />
              </a-form-item>
              <a-form-item label="质量" name="quality">
                <a-select v-model:value="pixverseForm.quality" placeholder="选择质量">
                  <a-select-option value="360p">360p</a-select-option>
                  <a-select-option value="540p">540p</a-select-option>
                  <a-select-option value="720p">720p</a-select-option>
                </a-select>
              </a-form-item>

              <a-form-item>
                <a-button type="primary" @click="callPixverseAPI" :loading="pixverseLoading">
                  调用接口
                </a-button>
              </a-form-item>
            </a-form>

            <div v-if="pixverseResult" class="result-section">
              <a-divider>调用结果</a-divider>
              <div class="result-card">
                <div class="result-header">
                  <span class="result-title">返回结果</span>
                </div>
                <div class="result-body">
                  <p><strong>Video ID:</strong> {{ pixverseResult.video_id }}</p>
                </div>
              </div>
            </div> 
          </a-card>

          <a-card title="Pixverse轮询任务" :bordered="false" class="tool-card" style="margin-top: 20px;">
            <p class="page-desc">轮询Pixverse任务结果。</p>

            <a-form :model="pixversePollForm" layout="vertical">
              <a-form-item label="Video ID" name="videoId" required>
                <a-input v-model:value="pixversePollForm.videoId" placeholder="请输入Video ID" />
              </a-form-item>
              <a-form-item>
                <a-button type="primary" @click="callPixversePollAPI" :loading="pixversePollLoading">
                  轮询结果
                </a-button>
              </a-form-item>
            </a-form>

            <div v-if="pixversePollResult" class="result-section">
              <a-divider>轮询结果</a-divider>
              <div class="result-card">
                <div class="result-header">
                  <span class="result-title">返回结果</span>
                </div>
                <div class="result-body">
                  <div v-if="pixversePollResult.result" class="result-content">
                    <p><strong>任务状态:</strong> {{ pixversePollResult.status }}</p>
                    <p v-if="pixversePollResult.result"><strong>视频地址:</strong> {{ pixversePollResult.result }}</p>
                    <div v-if="pixversePollResult.result" class="video-player">
                      <video :src="pixversePollResult.result" controls style="width: 100%; max-width: 500px;" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a-card>
        </div>
      </a-tab-pane>
    </a-tabs>

    <a-image
      v-if="webpURL"
      :src="webpURL"
      style="display: none"
      :preview="{ visible: previewVisible, onVisibleChange: (v) => (previewVisible = v) }"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { FullscreenOutlined, DownloadOutlined } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import ConvertVideo from '@/components/ConvertVideo.vue'
import UploadVideo from '@/components/UploadVideo.vue'
import Upload from '@/components/Upload.vue'
import request from '@/utils/request'

export default defineComponent({
  components: {
    ConvertVideo,
    FullscreenOutlined,
    DownloadOutlined,
    UploadVideo,
    Upload,
  },
  data() {
    return {
      activeTab: 'video',
      videoURL: '',
      webpURL: '',
      previewVisible: false,
      pixverseForm: {
        oriVideoPath: '',
        referImgPath: '',
        prompt: '',
        maskURL: '',
        quality: '540p',
      },
      pixverseResult: null,
      pixverseLoading: false,
      pixversePollForm: {
        videoId: '',
      },
      pixversePollResult: null,
      pixversePollLoading: false,
    }
  },
  methods: {
    downloadWebp() {
      if (!this.webpURL) return
      const a = document.createElement('a')
      a.href = this.webpURL
      a.download = 'converted.webp'
      a.target = '_blank'
      a.click()
    },

    async callPixverseAPI() {
      if (!this.pixverseForm.prompt) {
        message.error('请输入提示词')
        return
      }
      
      this.pixverseLoading = true
      try {
        const payload = {
          ori_video_path: this.pixverseForm.oriVideoPath,
          refer_img_path: this.pixverseForm.referImgPath,
          prompt: this.pixverseForm.prompt,
          mask_url: this.pixverseForm.maskURL,
          quality: this.pixverseForm.quality,
        }
        
        const response = await request.post('/api/video/edit', payload)
        this.pixverseResult = response.data
      } catch (error) {
        console.error('调用Pixverse API失败:', error)
        message.error('调用接口失败，请重试')
      } finally {
        this.pixverseLoading = false
      }
    },
    async callPixversePollAPI() {
      if (!this.pixversePollForm.videoId) {
        message.error('请输入Video ID')
        return
      }
      
      this.pixversePollLoading = true
      try {
        const response = await request.get(`/api/video/result/${this.pixversePollForm.videoId}`)
        this.pixversePollResult = response.data
      } catch (error) {
        console.error('轮询Pixverse任务结果失败:', error)
        message.error('轮询接口失败，请重试')
      } finally {
        this.pixversePollLoading = false
      }
    },
  },
})
</script>

<style scoped>
.creation-tools-page {
  padding: 24px;
}

.tool-wrap {
  max-width: 860px;
}

.tool-card {
  margin-top: 4px;
}

.page-desc {
  color: #888;
  margin-bottom: 20px;
  font-size: 13px;
}

.result-section {
  margin-top: 8px;
}

.media-row {
  display: flex;
  gap: 16px;
}

.media-card {
  flex: 1;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
  display: flex;
  flex-direction: column;
}

.media-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.media-title {
  font-size: 13px;
  font-weight: 600;
}

.result-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
  display: flex;
  flex-direction: column;
}

.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.result-title {
  font-size: 13px;
  font-weight: 600;
}

.result-body {
  padding: 14px;
  background: #fff;
  font-size: 13px;
}

.result-body p {
  margin: 8px 0;
  color: #333;
}

.result-content {
  margin: 10px 0;
}

.video-player {
  margin: 16px 0;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 8px;
}

.result-json {
  margin-top: 16px;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.4;
  max-height: 300px;
  overflow-y: auto;
}

.media-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.media-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  min-height: 240px;
  background: #f5f5f5;
}

.media-img {
  cursor: zoom-in;
}

.media-content {
  width: 100%;
  max-height: 320px;
  border-radius: 4px;
  object-fit: contain;
}

.media-footer {
  padding: 8px 14px;
  font-size: 11px;
  color: #aaa;
  word-break: break-all;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  line-height: 1.5;
}
</style>
